import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { location, programs } from '@cwos/api-client';
import { ErrorPanel, SkeletonSet } from '@cwos/ui-admin';
import { SettingsField } from '../settings/common.js';
import { useNav } from '../navigation.js';
import {
  formatScheduleInstant,
  modeLabel,
  organizationPendingNote,
  PRECEDENCE_SENTENCE,
  PROGRAM_ADD_OVERRIDE_HINT,
  PROGRAM_ADD_OVERRIDE_LABEL,
  PROGRAM_OVERRIDE_EDIT_LABEL,
  programEditLinkLabel,
  programHasScheduledChange,
} from './copy.js';
import { messageOf } from './policyForm.js';
import { programPolicyHref } from './ProgramPolicyPage.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Per-program policy overrides (design doc §11: the precedence already exists
 * server-side; this section EXPOSES it). The org default row is listed first,
 * then every program whose resolved policy comes from its OWN row
 * (`source === 'program'`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THIS IS A LIST. THE EDITING HAPPENS ON THE PROGRAM'S OWN PAGE.
 * ═══════════════════════════════════════════════════════════════════════════
 * It used to be a list AND an editor: a mode dropdown and an Update button per
 * row, which could express one of the ten policy values and none of the acts —
 * no interval, no accuracy floor, no retention window, no clock or exception
 * rules, and no schedule. When a revision was waiting on a program's scope the
 * row disabled itself and printed a sentence saying so, because a dropdown
 * cannot state a relationship to a scheduled revision.
 *
 * The founder's REVREL follow-up ruling of 2026-09-08 says that disabled state
 * was acceptable "ONLY AS A TEMPORARY FAIL-CLOSED CONDITION", and that program
 * overrides "must not become a weaker scheduling surface than organization-level
 * policy". So the dropdown is GONE rather than extended, and the row links to
 * `ProgramPolicyPage` — the same form, the same schedule control and the same
 * four relationships the organization has.
 *
 * KEEPING BOTH WOULD HAVE BEEN THE WORSE ANSWER. Two editors for one row, one
 * of which silently means `scheduledEffectiveAt: null` and cannot answer the
 * relationship question, is exactly the divergence the ruling forbids — and the
 * quick one would still have had to disable itself, which is the fail-closed
 * placeholder it is supposed to replace.
 *
 * Removing an override stays HERE, because it is not a policy value: the real
 * `DELETE /location/policy?programId=...` (F192) drops the row so the program
 * falls back to the organization default, then the product default — the
 * resolver's own precedence, never a copy of the org values into a lingering
 * program row (that copy is exactly the drift the precedence rule exists to
 * prevent). The route is program-only by design: the org default row is not
 * deletable, so there is deliberately no remove control on the "Everyone" line.
 *
 * The override rows come from the only read the API offers — the resolved
 * policy per program — so this is one request per program. Program counts here
 * are tens, not thousands; if that changes, the honest fix is a real
 * list-policies endpoint (repository.listPolicies exists, unexposed).
 */

interface OverrideRow {
  program: programs.Program;
  policy: location.EffectivePolicy;
  /**
   * A change published for THIS PROGRAM'S scope whose instant has not arrived.
   * null = the server answered and there is none. Read for the same reason the
   * org form reads it: this list resolves to the version IN FORCE, so without it
   * a program with a scheduled change looks identical to one with none.
   *
   * DERIVED FROM `pendingRevisions` AND NOT FROM `pending`. The two answer
   * different questions and at program scope they can disagree: `pending` is the
   * next change that alters what governs this program, which for a program with
   * no override of its own is the ORGANIZATION's revision. A row that printed
   * that as "a change is scheduled for this program" would be attributing
   * somebody else's act to a program it will not even reach once the program has
   * a policy. `pendingRevisions` is exactly the set waiting on the program's own
   * scope; its earliest member is this row's answer, and an empty list is an
   * absence rather than an unknown.
   */
  pending: location.PendingPolicyChange | null;
  /**
   * The ORGANIZATION's own waiting revision, when this program does not
   * override and would therefore receive it. Rendered as the organization's,
   * with no controls — see `organizationPendingNote`.
   */
  inherited: location.PendingPolicyChange | null;
}

export function ProgramOverrides({
  orgPolicy,
  canManage,
}: {
  orgPolicy: location.EffectivePolicy;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const navigate = useNav();
  const query = useQuery({
    queryKey: ['location', 'policy', 'program-overrides'],
    queryFn: async (): Promise<OverrideRow[]> => {
      const listed = await programs.list({ status: 'active' });
      return Promise.all(
        listed.programs.map(async (program) => {
          const resolved = await location.effectivePolicyWithSchedule({ programId: program.id });
          // THE SCOPE'S OWN SET, EARLIEST FIRST — the server sends it in
          // arrival order and this reads the first rather than re-sorting it.
          const own = resolved.pendingRevisions[0] ?? null;
          const inherited =
            resolved.pending !== null &&
            !resolved.pendingRevisions.some((r) => r.versionId === resolved.pending!.versionId)
              ? resolved.pending
              : null;
          return { program, policy: resolved.policy, pending: own, inherited };
        }),
      );
    },
  });

  const [mutateError, setMutateError] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: (programId: string) => location.deletePolicy({ programId }),
    onSuccess: () => {
      setMutateError(null);
      void qc.invalidateQueries({ queryKey: ['location', 'policy'] });
    },
    onError: (err: unknown) => setMutateError(messageOf(err)),
  });

  if (query.isLoading) return <SkeletonSet surface="table" rows={3} />;
  if (query.isError && !query.data) {
    return (
      <LoadFailure
        error={query.error}
        message="We couldn’t load program policies just now."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const rows = query.data ?? [];
  const overrides = rows.filter((row) => row.policy.source === 'program');
  const withoutOverride = rows.filter((row) => row.policy.source !== 'program');

  return (
    <div style={{ display: 'grid', gap: 'var(--space-12)' }}>
      <p className="rn-muted">
        Precedence: <span data-testid="precedence">{PRECEDENCE_SENTENCE}</span>
      </p>

      <ul
        style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-8)' }}
      >
        <li style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'baseline' }}>
          <span style={{ fontWeight: 600 }}>Everyone (organization default)</span>
          <span className="rn-muted">
            {modeLabel(orgPolicy.mode)}
            {orgPolicy.source === 'product_default' ? ' — product default, nothing stored yet' : ''}
          </span>
        </li>
        {overrides.map((row) => (
          <OverrideItem
            key={row.program.id}
            row={row}
            canManage={canManage}
            busy={remove.isPending}
            onOpen={() => navigate(programPolicyHref(row.program.id))}
            onRemove={() => {
              if (
                window.confirm(
                  `Remove the override for ${row.program.name}? It will follow the organization default again.`,
                )
              ) {
                remove.mutate(row.program.id);
              }
            }}
          />
        ))}
        {overrides.length === 0 ? (
          <li className="rn-muted">
            No program has its own policy — every program follows the organization default.
          </li>
        ) : null}
      </ul>

      {mutateError ? <ErrorPanel message={mutateError} /> : null}

      {canManage && withoutOverride.length > 0 ? (
        <AddOverride
          candidates={withoutOverride}
          onOpen={(programId) => navigate(programPolicyHref(programId))}
        />
      ) : null}
    </div>
  );
}

function OverrideItem({
  row,
  canManage,
  busy,
  onOpen,
  onRemove,
}: {
  row: OverrideRow;
  canManage: boolean;
  busy: boolean;
  onOpen: () => void;
  onRemove: () => void;
}) {
  return (
    <li style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 600 }}>{row.program.name}</span>
      <span className="rn-muted">{modeLabel(row.policy.mode)}</span>
      {/*
        WHAT IS ALREADY COMING FOR THIS PROGRAM. Absent this line the row is
        indistinguishable from one with nothing scheduled — and it is the
        program's OWN pending revision, never the organization's.
      */}
      {row.pending ? (
        <span className="rn-muted">
          {programHasScheduledChange(formatScheduleInstant(row.pending.governsFrom))}
        </span>
      ) : null}
      {/*
        THE LINK IS THE CONTROL, and it is offered to a read-only holder too:
        the editor renders every field disabled for a principal without
        `location:manage`, exactly as the four organization pages do, so hiding
        it would hide a READ a `location:read` holder is entitled to.
      */}
      <a
        href={programPolicyHref(row.program.id)}
        aria-label={programEditLinkLabel(row.program.name)}
        onClick={(e) => {
          e.preventDefault();
          onOpen();
        }}
      >
        {PROGRAM_OVERRIDE_EDIT_LABEL}
      </a>
      {canManage ? (
        <button
          type="button"
          className="rn-btn-quiet"
          disabled={busy}
          onClick={onRemove}
          aria-label={`Remove the override for ${row.program.name}`}
        >
          Remove
        </button>
      ) : null}
    </li>
  );
}

/**
 * A PROGRAM THAT DOES NOT OVERRIDE YET — and the organization change that WOULD
 * reach it.
 *
 * This picker no longer saves. It navigates: an override is created by saving
 * the program's own policy page, where the operator can see every value they
 * are about to store and state a schedule for them. The old "Add override"
 * button wrote a whole `location_policy` row from a mode dropdown with
 * `scheduledEffectiveAt: null`, which is a policy for a set of workers chosen
 * from one field.
 */
function AddOverride({
  candidates,
  onOpen,
}: {
  candidates: OverrideRow[];
  onOpen: (programId: string) => void;
}) {
  const [programId, setProgramId] = useState('');
  const chosen = candidates.find((row) => row.program.id === programId) ?? null;
  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <div style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'end', flexWrap: 'wrap' }}>
        <SettingsField label="Program" hint={PROGRAM_ADD_OVERRIDE_HINT}>
          <select value={programId} onChange={(e) => setProgramId(e.target.value)}>
            <option value="">Choose a program…</option>
            {candidates.map((row) => (
              <option key={row.program.id} value={row.program.id}>
                {row.program.name}
              </option>
            ))}
          </select>
        </SettingsField>
        <button
          type="button"
          className="rn-btn-quiet"
          disabled={programId === ''}
          onClick={() => onOpen(programId)}
        >
          {PROGRAM_ADD_OVERRIDE_LABEL}
        </button>
      </div>
      {/*
        THE ORGANIZATION'S SCHEDULED CHANGE, ATTRIBUTED TO THE ORGANIZATION.
        A program with no override receives the organization's pending revision,
        and `GET /location/policy/effective` reports it in `pending` while
        `pendingRevisions` (the set a save to THIS program would decide about)
        is empty. Saying so here is the difference between "your organization is
        about to change this program's rules" — true — and "this program has a
        change scheduled", which would attribute an act to the wrong scope and
        offer choices the server would refuse with NOT_PENDING.
      */}
      {chosen?.inherited ? (
        <p className="rn-muted" role="note">
          {organizationPendingNote(formatScheduleInstant(chosen.inherited.governsFrom))}
        </p>
      ) : null}
    </div>
  );
}
